import * as React from 'react';
import { Button, Typography, TypographyType } from '@/components/common';
import clsx from 'clsx';
import { ActionRequired, ActionRequiredOpenHandler } from '@/interfaces';
import { InfoCircle } from '@/public/icons/tsx';
import { ByDate, TimerLabel } from './action-required/ActionRequiredMeta';
import {
	getActionRequiredButtonLabel,
	getActionRequiredHref,
	getActionRequiredIds,
	invalidRoutes,
} from '@/utils/actionRequired';
import { useRouter } from 'next/router';

interface ActionRequiredItemProps {
	item: ActionRequired;
	dateTypography?: TypographyType;
	withTitle?: boolean;
	withIcon?: boolean;
	isDashboard?: boolean;
	setActionRequiredSelected: (item: ActionRequired) => void;
	handleClickOptionToOpen: ActionRequiredOpenHandler;
}

export const ActionRequiredItem: React.FC<ActionRequiredItemProps> = ({
	item,
	dateTypography = 'regular-16',
	withIcon,
	withTitle,
	isDashboard,
	setActionRequiredSelected,
	handleClickOptionToOpen,
}) => {
	return (
		<li className="flex flex-col lg:justify-between gap-1 lg:gap-2 mb-2  relative">
			<div className="flex items-center gap-2">
				{isDashboard && (
					<span
						className="w-3 h-3 rounded-full flex-shrink-0 bg-[#FF0043]"
						style={{ border: '1.5px solid #FFFFFF' }}
					/>
				)}
				<ActionRequiredItemDesktop
					dateTypography={dateTypography}
					item={item}
					withIcon={withIcon}
					withTitle={withTitle}
					isDashboard={isDashboard}
					setActionRequiredSelected={setActionRequiredSelected}
					handleClickOptionToOpen={handleClickOptionToOpen}
				/>
			</div>
			<ActionRequiredItemMobile
				item={item}
				withIcon={withIcon}
				setActionRequiredSelected={setActionRequiredSelected}
				handleClickOptionToOpen={handleClickOptionToOpen}
			/>
		</li>
	);
};

const ActionRequiredItemDesktop: React.FC<ActionRequiredItemProps> = ({
	dateTypography,
	handleClickOptionToOpen,
	item,
	setActionRequiredSelected,
	isDashboard,
	withIcon,
	withTitle,
}) => {
	const router = useRouter();
	const href = getActionRequiredHref(item);
	const { caseId, appId } = getActionRequiredIds(item);

	return (
		<div className="hidden lg:flex flex-col w-full gap-1 p-2 bg-grey-2 rounded-2xl">
			{withIcon && (
				<InfoCircle width="20" height="20" className="flex-shrink-0" />
			)}
			{withTitle && (
				<Typography type="semibold-18" className="lg:whitespace-nowrap">
					{item.case?.case_title}
				</Typography>
			)}

			<div className="flex items-center gap-2">
				<div className="shrink-0">
					<Button
						label={getActionRequiredButtonLabel(item)}
						decoration="line-primary"
						size="fit"
						className="!py-1 !w-fit"
						href={href}
						onClick={() => {
							handleClickOptionToOpen(
								item.action_type,
								item.id,
								caseId,
								appId,
								item.url,
								item.criteria?.instructions
							);
							setActionRequiredSelected(item);
						}}
						disabled={invalidRoutes.some((route) =>
							router.pathname.includes(route)
						)}
					/>
				</div>
				<div
					className={clsx('flex justify-between items-center w-full', {
						'flex-col !items-end': !isDashboard,
					})}
				>
					<ByDate item={item} dateTypography={dateTypography} />
					<TimerLabel item={item} />
				</div>
			</div>
		</div>
	);
};

const ActionRequiredItemMobile: React.FC<ActionRequiredItemProps> = ({
	handleClickOptionToOpen,
	item,
	setActionRequiredSelected,
	withIcon,
}) => {
	const router = useRouter();
	const href = getActionRequiredHref(item);
	const { caseId, appId } = getActionRequiredIds(item);

	return (
		<div className="items-center gap-x-2 lg:hidden">
			{withIcon && (
				<InfoCircle width="20" height="20" className="flex-shrink-0" />
			)}
			<div className="flex flex-col w-full gap-3">
				<div className="flex gap-2 justify-between items-center">
					<ByDate item={item} />
					<TimerLabel item={item} />
				</div>
				<Button
					label={getActionRequiredButtonLabel(item)}
					decoration="line-primary"
					size="fit"
					className="!py-1 !w-fit !text-left"
					href={href}
					onClick={() => {
						handleClickOptionToOpen(
							item.action_type,
							item.id,
							caseId,
							appId,
							item.url,
							item.criteria?.instructions
						);
						setActionRequiredSelected(item);
					}}
					disabled={invalidRoutes.some((route) =>
						router.pathname.includes(route)
					)}
				/>
			</div>
		</div>
	);
};
